import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useMemo } from "react";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { Spinner } from "@/components/ui/spinner";
import OneVOneHistory from "@/components/OneVOneHistory";
import { useMatchmakingHistory } from "@/lib/hooks/useApi";
import useInfiniteScroll from "@/lib/use-infinite-scroll";

function MatchHistoryPage() {
  const router = useRouter();

  // Passing `null` skips the userId param → global 1v1 history.
  const {
    data,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useMatchmakingHistory(null);

  // pending_create + expired are server-internal queue states (tx confirming
  // or never landed) and shouldn't show in the public feed.
  const rows = useMemo(() => {
    const pages = data?.pages ?? [];
    return pages
      .flatMap((p) => p.rows ?? [])
      .filter(
        (r) =>
          r.kind !== "queue" ||
          (r.status !== "pending_create" && r.status !== "expired"),
      );
  }, [data]);

  const { lastElementRef } = useInfiniteScroll({
    isLoading,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="bg-[#1D2A52] flex flex-1 flex-col h-full overflow-hidden p-1 gap-4 min-h-0">
      <FloatingLeftButton
        onClick={() => router.navigate({ to: "/home" })}
        className="absolute left-0 top-4 z-50"
        icon={
          <svg
            className="w-4 h-4"
            viewBox="0 0 9 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
              fill="white"
            />
          </svg>
        }
      />

      <div className="flex-1 flex flex-col p-1 gap-4 w-full min-h-0 mt-10">
        <div className="text-center flex flex-col gap-2">
          <p className="text-white text-3xl font-extrabold text-shadow-black">
            1v1 History
          </p>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden min-h-0 mt-3">
          <OneVOneHistory
            rows={rows}
            walletAddress="global"
            hasMore={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            lastElementRef={lastElementRef}
            onRefetch={() => refetch()}
            isGlobal
          />
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/(game)/match/history")({
  component: MatchHistoryPage,
});
